import { getContext, setContext } from "svelte";
import { derived, writable } from "svelte/store";
import { teamStore } from "../../../api-stores/license-store";

const ADMIN_PANEL_CTX = Symbol("ADMIN_LICENSE_CTX");

const createContext = () => {
  teamStore.once();

  // This is for locally editing the team before firing off requests(s) to the backend to make the changes.
  const modifiedTeamStore = writable({});
  teamStore.subscribe((x) =>
    modifiedTeamStore.set(JSON.parse(JSON.stringify(x))),
  );

  const modulesStore = derived(teamStore, ($innerTeamStore) => {
    const { licenses = [] } = $innerTeamStore;
    return licenses.map((license) => ({
      kind: license.kind,
      title: license.title,
      permissionObject: license.permissionObject,
      priceKey: license.priceKey,
    }));
  });

  const rows = derived(
    [modifiedTeamStore, teamStore],
    ([$modifiedTeamStore, $innerTeamStore]) => {
      if (!$modifiedTeamStore?.users) return [];

      const { licenses = [], users = [] } = $modifiedTeamStore;
      const originalUsers = $innerTeamStore.users || [];
      const available = Object.fromEntries(
        licenses.map((l) => [l.kind, l.activeCount < (l.total || 0)]),
      );

      return users
        .filter((u) => !["alias", "viewer"].includes(u.kind)) // can remove once license table handles viewer/alias
        .map((user) => {
          // Find all groups the user belongs to
          const groupIds = Object.entries($modifiedTeamStore.teamGroups || {})
            .filter(([, group]) =>
              group.users.some((u) => u.userId === user.userId),
            )
            .map(([id]) => id);

          const userLicensesByKind = Object.fromEntries(
            licenses.map((l) => [l.kind, user.licenses.includes(l.kind)]),
          );
          const originalUser =
            originalUsers.find((u) => u.userId === user.userId) || {};
          const originalUserLicenseByKind = Object.fromEntries(
            ($innerTeamStore.licenses || []).map((l) => [
              l.kind,
              originalUser.licenses.includes(l.kind),
            ]),
          );

          return {
            email: user.userEmails[0],
            name: user.name,
            photo: user.picture || null,
            userId: user.userId,
            teamId: $modifiedTeamStore.teamId,
            groupIds,
            userKind: user.kind,
            licenses: userLicensesByKind,
            originalLicenses: originalUserLicenseByKind,
            available,
          };
        });
    },
  );

  const licensesStore = derived([teamStore], ([$teamStore]) => {
    if (!$teamStore?.users) return {};

    const { licenses = [] } = $teamStore;
    const available = Object.fromEntries(
      licenses.map((l) => [l.kind, (l.activeCount || 0) < (l.total || 0)]),
    );

    return Object.fromEntries(
      licenses.map((license) => [
        license.kind,
        {
          ...license,
          available: available[license.kind],
          price: $teamStore[`${license.kind}Price`] || 0, // do we want to allow purchasing completely fresh licenses? If so need the pricing matrix here
        },
      ]),
    );
  });

  const showAddLicensesModalStore = writable(false);
  const licenseModalInitialAdditionStore = writable("");

  const currencySuffixStore = derived(
    teamStore,
    ($teamStore) =>
      ({
        gbp: " (£)",
        usd: " ($)",
      })[$teamStore.currency] || "",
  );

  return {
    modulesStore,
    modifiedTeamStore,
    showAddLicensesModalStore,
    licenseModalInitialAdditionStore,
    rows,
    licensesStore,
    currencySuffixStore,
  };
};

export const createAdminPanelContext = () => {
  const context = createContext();
  setContext(ADMIN_PANEL_CTX, context);
  return context;
};

export const getAdminPanelContext = () => getContext(ADMIN_PANEL_CTX);
